import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as moment from 'moment-timezone';
import { Observable, throwError } from 'rxjs';
import { catchError, concatMap, filter, take, tap } from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { ToastService } from '@libs/src/toast/toast.service';
import { ZendeskService } from '@libs/src/zendesk/zendesk.service';

import { DialogSessionsSettingsComponent } from './dialog-sessions-settings/dialog-sessions-settings.component';
import { DialogConfirmPromptComponent } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.component';

import {
  CONTACT_HELP_DESK_BUTTON_CONFIG,
  DELETE_PROFILE_BUTTON_CONFIG,
  deleteAccountSettingsConfig,
  NOTIFICATION_EMAIL_SETTINGS,
  NotificationSettingsConfig,
  SESSIONS_SETTINGS_CONFIG,
  timezoneSettingsConfig,
  UPDATE_TIMEZONE_BUTTON_CONFIG,
} from './settings.helpers';
import { Account } from '@libs/src/models/account.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { SetUser } from '@libs/src/user/user.actions';
import { IAppState } from '@main-client/src/app/app.state';

import { FormGroupTemplate, SettingsFormType } from '../form/form.model';

import { DialogService } from '../dialog/dialog.service';
import { UserMutationService } from '../user/user-mutation.service';

import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import map from 'lodash-es/map';
import reduce from 'lodash-es/reduce';
import set from 'lodash-es/set';

@Injectable()
export class SettingsFormBuilderService {
  constructor(
    private readonly accountBackend: AccountBackend,
    private readonly dialogService: DialogService,
    private readonly store: Store<IAppState>,
    private readonly toast: ToastService,
    private readonly userMutationService: UserMutationService,
    private readonly zendesk: ZendeskService,
  ) {}

  getNotificationSettingsConfig(user: Account): FormGroupTemplate {
    return {
      forms: this.generateNotificationForms(user, NOTIFICATION_EMAIL_SETTINGS),
      title: {
        primaryIcon: 'notifications',
        text: 'Notifications',
      },
      type: 'static',
    };
  }

  getTimeZoneSettingsConfig(user: any): FormGroupTemplate {
    const updateTimeZoneAction = {
      ...UPDATE_TIMEZONE_BUTTON_CONFIG,
      callback: (form: any) => this._saveUser(form),
    };
    const config = timezoneSettingsConfig(user);
    return {
      actions: [updateTimeZoneAction],
      ...config,
      forms: config.forms.map((timezoneForm: any) => {
        return {
          ...timezoneForm,
          callback: (form: any) => this.applyTimezoneSelectOptions(form),
        };
      }),
    };
  }

  getSessionsSettingsConfig(tenant: Tenant, user: any): FormGroupTemplate {
    return {
      ...SESSIONS_SETTINGS_CONFIG,
      headerAction: {
        ...SESSIONS_SETTINGS_CONFIG.headerAction,
        callback: () => this._openSessionsDialog(tenant, user),
      },
    };
  }

  getDeleteAccountSettingsConfig(user: any): FormGroupTemplate {
    const deleteProfileAction = {
      ...DELETE_PROFILE_BUTTON_CONFIG,
      callback: () =>
        this._deleteProfileConfirmPrompt(user, deleteProfileAction),
    };
    const contactHelpDeskButtonConfig = {
      ...CONTACT_HELP_DESK_BUTTON_CONFIG,
      callback: () => this.zendesk.showZendesk(),
    };
    return {
      actions: get(user, 'isAdmin')
        ? [contactHelpDeskButtonConfig]
        : [deleteProfileAction],
      ...deleteAccountSettingsConfig(user),
    };
  }

  private _openSessionsDialog(tenant: Tenant, user: any): void {
    const config = {
      data: {
        tenant,
        sessions: get(user, 'sessions'),
        deleteSession: (sessionId: string) =>
          this.userMutationService.deleteSession(
            get(user, 'sessions'),
            sessionId,
          ),
      },
    };
    this.dialogService.openDialogComponent({
      config,
      component: DialogSessionsSettingsComponent,
    });
  }

  private _saveUser(form: any): void {
    this._patchUser(form).subscribe();
  }

  private _patchUser(form: any): Observable<any> {
    const update = this._unflattenForm(form);
    return this.store.select('user').pipe(
      filter((userState: Account) => !isEmpty(userState)),
      take(1),
      concatMap((userState: Account) => {
        return this.accountBackend.patchAccount(update).pipe(
          tap(() => {
            this.toast.showSuccess('Update successful!');
            this.store.dispatch(SetUser(update));
          }),
          catchError((response: any) => {
            const errorMessage = get(
              response,
              'error.message',
              response.message,
            );
            this.toast.showError(errorMessage);
            this.store.dispatch(SetUser(userState));
            return throwError(response);
          }),
        );
      }),
    );
  }

  private _deleteProfileConfirmPrompt(user: any, actionConfig: any): void {
    const config = {
      width: '400px',
      data: {
        title: 'Deleting Profile',
        content: 'Are you sure?',
        cancel: 'Cancel',
        confirm: 'Okay',
      },
    };
    const dialogRef = this.dialogService.openDialogComponent({
      config,
      component: DialogConfirmPromptComponent,
    });
    dialogRef.afterClosed().subscribe((data: any) => {
      if (!data) {
        return;
      }
      actionConfig.disable = true;
      actionConfig.text = 'Loading...';
      this.userMutationService
        .deleteProfile(() => {
          window.location.href = `/delete-feedback?accountId=${user._id}`;
        })
        .subscribe();
    });
  }

  private generateNotificationForms(
    user: Account,
    settings: NotificationSettingsConfig,
  ) {
    return map(
      settings.forms,
      (
        { placeholder, updateKey }: { placeholder: string; updateKey: string },
        index: number,
      ) => {
        return {
          data: {
            placeholder,
          },
          label: settings.label,
          model: get(user, updateKey, false),
          showLabel: isEqual(0, index),
          type: SettingsFormType.checkbox,
          updateKey,
          callback: (form: any) => this._saveUser(form),
        };
      },
    );
  }

  private _unflattenForm(form: any) {
    return reduce(form, (result, value, key) => set(result, key, value), {});
  }

  public generatePersonalSettings(tenant: Tenant, user: Account): any {
    return [
      {
        panels: [
          this.getNotificationSettingsConfig(user),
          this.getTimeZoneSettingsConfig(user),
          this.getSessionsSettingsConfig(tenant, user),
        ],
        type: 'card',
      },
      {
        panels: [this.getDeleteAccountSettingsConfig(user)],
        type: 'expansion',
      },
    ];
  }

  applyTimezoneSelectOptions(form: any): void {
    if (1 < form.data.options.length) {
      return;
    }
    form.data.options = moment.tz
      .names()
      .map((timezone: any) => ({ value: timezone, text: timezone }));
  }
}
